<script setup lang="ts">
/**
 * Singbac Incorporation (Pre) — FORM 45B: Consent to Act as Secretary
 * (Companies Act 1967, section 173C(b) / section 171(1AA)). 원문 서식 그대로 재현.
 * · 자격 항목 (i)…(vi) 는 sec_qual 선택 그룹(§2b) — 하나만 ☒. 화면 값은 fill['sec_qual'] 에 저장,
 *   normalize 가 [[if sec_qual_i]]…[[endif]] 용 sec_qual_<key>='Yes' 로 펼친다.
 * · 두 각주(†, *)는 원문 그대로 재현.
 */
import { computed } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import SgIncPreSigners from './SgIncPreSigners.vue';
import { useCddFill } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();

/** Form 45B — section 171(1AA) 자격 항목 (원문 (i) … (vi) 그대로). docx 변수 = sec_qual_<key>. */
const SEC_QUAL_ITEMS: { key: string; roman: string; text: string }[] = [
  { key: 'i', roman: '(i)', text: 'a secretary of a company for at least 3 years of the 5 years immediately preceding the abovementioned date of my appointment as secretary of the abovenamed company.' },
  { key: 'ii', roman: '(ii)', text: 'a qualified person under the Legal Profession Act 1966.' },
  { key: 'iii', roman: '(iii)', text: 'a public accountant.' },
  { key: 'iiia', roman: '(iiia)', text: 'a member of the Institute of Singapore Chartered Accountants (formerly known as the Institute of Certified Public Accountants of Singapore).' },
  { key: 'iv', roman: '(iv)', text: 'a member of the Chartered Secretaries Institute of Singapore.' },
  { key: 'v', roman: '(v)', text: 'a member of the Association of International Accountants (Singapore Branch).' },
  { key: 'vi', roman: '(vi)', text: 'a member of The Institute of Company Accountants, Singapore.' },
];

const selectedQual = computed(() => String(fill.values['sec_qual'] || '').trim());
function selectSecQual(key: string) {
  fill.setValue('sec_qual', selectedQual.value === key ? '' : key);
}
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT 1967</div>
      <div class="doc-sec-title" style="margin:6px 0 2px">FORM 45B</div>
      <div class="doc-lbl-title">SECTION 173C(b) CONSENT TO ACT AS SECRETARY</div>
    </div>

    <p class="doc-p">Name of company: <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p">Company No: <CddVarField field-key="company_registration_no" label="Company Registration No." /></p>

    <p class="doc-p">
      1.&nbsp;&nbsp;I, the undermentioned person, hereby consent to act as a secretary of the abovenamed company with
      effect from <CddVarField field-key="consent_effective_date" label="Consent Effective Date" /> (date)
    </p>

    <p class="doc-p">
      2.&nbsp;&nbsp;I am a qualified person under section 171(1AA) of the Companies Act 1967 (&ldquo;the Act&rdquo;) by
      virtue of my being &mdash;
    </p>

    <p
      v-for="q in SEC_QUAL_ITEMS"
      :key="q.key"
      class="doc-p qual-row doc-toggle"
      :class="{ off: selectedQual !== q.key }"
      :title="selectedQual === q.key ? 'selected — click to clear' : 'click to select this qualification'"
      @click="selectSecQual(q.key)"
    >
      <span class="qual-mark">{{ selectedQual === q.key ? '☒' : '☐' }}</span>
      <span class="qual-txt">{{ q.roman }} {{ q.text }}</span>
    </p>

    <p class="doc-p">
      3.&nbsp;&nbsp;There is currently no debarment order under section 155B of the Act in force against me.
    </p>

    <table class="cdd-tbl" style="margin-top:12px">
      <tbody>
        <tr>
          <td class="cdd-lcell">Name</td>
          <td><CddVarField field-key="secretary_name" label="Secretary Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Residential Address</td>
          <td><CddVarField field-key="secretary_residential_address" label="Secretary Residential Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Contact Address</td>
          <td><CddVarField field-key="secretary_contact_address" label="Secretary Contact Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">*NRIC/Passport No</td>
          <td><CddVarField field-key="secretary_identification_no" label="Secretary NRIC / Passport No." /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nationality</td>
          <td><CddVarField field-key="secretary_nationality" label="Secretary Nationality" /></td>
        </tr>
      </tbody>
    </table>

    <!-- 서명 블록 [[Signers]] — 이 부수의 비서 본인(수동 추가/편집·T-Sign) -->
    <div class="sign-block">
      <div class="sign-lbl">Signature</div>
      <SgIncPreSigners name-key="secretary_name" title="Secretary" board-label="" />
    </div>

    <p class="doc-p" style="margin-top:10px">Dated this <CddVarField field-key="dated_on" label="Dated On" /></p>

    <div class="doc-attn">
      <p>
        † To be completed by secretaries of public companies only or by secretaries of private companies appointed
        under section 171(1AB) of the Act.
      </p>
      <p>* Delete where inapplicable.</p>
    </div>
  </div>
</template>

<style scoped>
.qual-row { display: flex; gap: 8px; align-items: flex-start; }
.qual-row.off { background: transparent; box-shadow: none; font-weight: 400; }
.qual-mark { font-size: 15px; line-height: 1.3; flex: 0 0 auto; }
.qual-txt { flex: 1; }
.doc-attn p { margin: 0 0 4px; }
.sign-block { margin-top: 14px; }
.sign-lbl { font-weight: 700; margin-bottom: 6px; }
</style>
